import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { DynamicTranslationService } from "../Services/DynamicTranslationService ";
import dutchTranslation from "./translation_nl.json";
import englishTranslation from "./translation_en.json";
import secureLocalStorage from "react-secure-storage";
import * as StorageConstants from "../Models/SecureStorageConstants";
import { applyPrimeReactLocale } from "../Util/primeReactLocale";

const v9000TranslationBundles = [] as const;

/** Keys where static JSON has correct UTF-8; DB rows from V9000_0 may show mojibake (e.g. ðŸ'‹). */
const UTF8_STATIC_OVERLAY_KEYS = [
  "home.greeting",
  "home.alerts.empty",
  "alert.highlight.empty",
  "alert.assignee.unassigned",
  "alert.assignee.fallbackName",
  "processflow.create.header.stepProgress",
] as const;

const staticBundlesByLanguage: Record<string, Record<string, string>> = {
  nl: dutchTranslation as Record<string, string>,
  en: englishTranslation as Record<string, string>,
};

const applyUtf8StaticOverlays = (languageCode: string, bundle: Record<string, string>) => {
  const staticBundle = staticBundlesByLanguage[languageCode];
  if (!staticBundle) {
    return bundle;
  }
  const next = { ...bundle };
  for (const key of UTF8_STATIC_OVERLAY_KEYS) {
    const value = staticBundle[key];
    if (value) {
      next[key] = value;
    }
  }
  if (languageCode === "nl" && !next["alert.highlight.empty"]) {
    next["alert.highlight.empty"] = dutchTranslation["home.alerts.empty"];
  }
  if (languageCode === "en" && !next["alert.highlight.empty"]) {
    next["alert.highlight.empty"] = englishTranslation["home.alerts.empty"];
  }
  return next;
};

const mergeTranslationBundle = <T extends Record<string, string>>(base: T, languageCode = "nl") => {
  const merged = { ...base, ...Object.assign({}, ...v9000TranslationBundles) } as T;
  return applyUtf8StaticOverlays(languageCode, merged as Record<string, string>) as T;
};

const resources: Record<string, { translation: Record<string, string> }> = {
  nl: {
    translation: mergeTranslationBundle(dutchTranslation, "nl"),
  },
};

const cacheTranslations = (languageCode: string, translations: Record<string, string>) => {
  secureLocalStorage.setItem(StorageConstants.i18nCacheKey(languageCode), JSON.stringify(translations));
};

const loadCachedTranslations = (languageCode: string): Record<string, string> | null => {
  const cachedData = secureLocalStorage.getItem(StorageConstants.i18nCacheKey(languageCode))?.toString();
  return cachedData ? JSON.parse(cachedData) : null;
};

const buildTranslationObject = (
  translations: Array<{ parameter: string; value: string }>,
): Record<string, string> => translations.reduce(
  (acc: Record<string, string>, item) => {
    acc[item.parameter] = item.value;
    return acc;
  },
  {},
);

const applyLanguageBundle = async (
  languageCode: string,
  bundle: Record<string, string>,
  persist = true,
) => {
  const merged = mergeTranslationBundle(bundle, languageCode);
  resources[languageCode] = { translation: merged };
  if (persist) {
    cacheTranslations(languageCode, merged);
  }

  if (!i18next.isInitialized) {
    await i18next.use(initReactI18next).init({
      resources,
      lng: languageCode,
      fallbackLng: "nl",
      interpolation: {
        escapeValue: false,
      },
    });
    return;
  }

  i18next.addResourceBundle(languageCode, "translation", merged, true, true);
  await i18next.changeLanguage(languageCode);
};

const resolveImmediateBundle = (languageCode: string): { bundle: Record<string, string>; persist: boolean } => {
  const cachedTranslations = loadCachedTranslations(languageCode);
  if (cachedTranslations) {
    return { bundle: cachedTranslations, persist: true };
  }
  const staticBundle = staticBundlesByLanguage[languageCode] ?? dutchTranslation;
  return { bundle: staticBundle as Record<string, string>, persist: false };
};

let translationsRefreshInflight: Promise<void> | null = null;

const refreshTranslationsFromApi = (languageCode: string): Promise<void> => {
  if (translationsRefreshInflight) {
    return translationsRefreshInflight;
  }

  translationsRefreshInflight = (async () => {
    try {
      const translations = await new DynamicTranslationService().getTranslationsForUser(languageCode);
      if (translations.length === 0) {
        console.warn("Fetched translations are empty; keeping current bundle");
        return;
      }
      const cachedTranslations = loadCachedTranslations(languageCode);
      await applyLanguageBundle(
        languageCode,
        {
          ...(cachedTranslations ?? {}),
          ...buildTranslationObject(translations),
        },
        true,
      );
    } catch (fetchError) {
      console.warn("Could not refresh translations from API, using cache:", fetchError);
    }
  })().finally(() => {
    translationsRefreshInflight = null;
  });

  return translationsRefreshInflight;
};

const initializeI18n = async () => {
  const languageCode = navigator.language.split("-")[0].toLowerCase();
  applyPrimeReactLocale(languageCode);
  const { bundle, persist } = resolveImmediateBundle(languageCode);

  try {
    await applyLanguageBundle(languageCode, bundle, persist);
  } catch (error) {
    console.error("Error loading translations:", error);
    if (!i18next.isInitialized) {
      const fallback = mergeTranslationBundle(dutchTranslation, languageCode);
      resources[languageCode] = { translation: fallback };
      await i18next.use(initReactI18next).init({
        resources,
        lng: languageCode,
        fallbackLng: "nl",
        interpolation: {
          escapeValue: false,
        },
      });
    }
  }

  if (secureLocalStorage.getItem(StorageConstants.USER)) {
    void refreshTranslationsFromApi(languageCode);
  }

  return i18next;
};

export const i18nReady = initializeI18n();
export { initializeI18n };
export default i18next;
